import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import { evaluateTrustedHostMessageEvent } from '../utils/postMessageTrust';

export type TokenSource = 'host_bootstrap' | 'bridge_fetch' | 'none';

export interface ResolvedAccessToken {
  token: string | null;
  source: TokenSource;
}

const JWT_EXPIRY_SKEW_MS = 45_000;
const AUTH_DEBUG_INFO_ENABLED = process.env.NODE_ENV !== 'production';

let runtimeInitialized = false;
let inMemoryToken: string | null = null;
let inMemorySource: TokenSource = 'none';

const TRUSTED_AUTH_MESSAGE_TYPES = new Set([
  'HOST_BOOTSTRAP',
  'HOST_AUTH_RESULT',
  'AUTH_BOOTSTRAP',
  'AUTH_EVENT',
  'AUTH_TOKEN_RESPONSE'
]);

type HostedAuthMessageType = 'HOST_BOOTSTRAP' | 'HOST_AUTH_RESULT' | 'AUTH_BOOTSTRAP' | 'AUTH_EVENT' | 'AUTH_TOKEN_RESPONSE';

const HOSTED_AUTH_MESSAGE_TYPES: HostedAuthMessageType[] = [
  'HOST_BOOTSTRAP',
  'HOST_AUTH_RESULT',
  'AUTH_BOOTSTRAP',
  'AUTH_EVENT',
  'AUTH_TOKEN_RESPONSE',
];

const createZeroCounts = (): Record<HostedAuthMessageType, number> => ({
  HOST_BOOTSTRAP: 0,
  HOST_AUTH_RESULT: 0,
  AUTH_BOOTSTRAP: 0,
  AUTH_EVENT: 0,
  AUTH_TOKEN_RESPONSE: 0,
});

const hostMessageStats = {
  accepted: createZeroCounts(),
  rejected: createZeroCounts(),
};

const hostRejectReasonStats: Record<string, number> = {};
let firstTokenSourceLogged = false;
let hostStatsLogCounter = 0;
const HOST_STATS_LOG_INTERVAL = 10;

const toHostedAuthMessageType = (type: string | undefined): HostedAuthMessageType | null => {
  if (!type) return null;
  return HOSTED_AUTH_MESSAGE_TYPES.includes(type as HostedAuthMessageType)
    ? (type as HostedAuthMessageType)
    : null;
};

const recordHostMessageStat = (
  kind: 'accepted' | 'rejected',
  type: HostedAuthMessageType,
  reason?: string,
): void => {
  hostMessageStats[kind][type] += 1;
  if (reason) {
    hostRejectReasonStats[reason] = (hostRejectReasonStats[reason] ?? 0) + 1;
  }
};

const logHostMessageStats = (): void => {
  if (!AUTH_DEBUG_INFO_ENABLED) {
    return;
  }

  hostStatsLogCounter += 1;
  if (hostStatsLogCounter % HOST_STATS_LOG_INTERVAL !== 0) {
    return;
  }

  console.info('[DiagnosticsAuthDebug] host message stats', {
    accepted: hostMessageStats.accepted,
    rejected: hostMessageStats.rejected,
    rejectedReasons: hostRejectReasonStats,
    logInterval: HOST_STATS_LOG_INTERVAL,
  });
};

const isTrustedHostMessage = (event: MessageEvent<unknown>, type: string | undefined): boolean => {
  const hostedType = toHostedAuthMessageType(type);
  if (!hostedType || !TRUSTED_AUTH_MESSAGE_TYPES.has(hostedType)) {
    if (type) {
      console.warn('[DiagnosticsAuthDebug] host message rejected', {
        reason: 'missing_expected_type',
        type,
        origin: event.origin,
      });
    }
    return false;
  }

  const trustDecision = evaluateTrustedHostMessageEvent(event);
  if (!trustDecision.trusted) {
    recordHostMessageStat('rejected', hostedType, trustDecision.reason);
    console.warn('[DiagnosticsAuthDebug] host message rejected', {
      type: hostedType,
      reason: trustDecision.reason,
      origin: event.origin,
      expectedOrigin: trustDecision.expectedOrigin,
      allowedEmbeddedOrigins: trustDecision.allowedEmbeddedOrigins,
      embedded: window.parent !== window,
    });
    logHostMessageStats();
    return false;
  }

  recordHostMessageStat('accepted', hostedType);
  logHostMessageStats();
  return true;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const getTokenExpiryMs = (token: string): number | null => {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;

  const exp = payload.exp;
  if (typeof exp !== 'number') return null;
  return exp * 1000;
};

export const isTokenExpired = (token: string): boolean => {
  const expMs = getTokenExpiryMs(token);
  if (!expMs) return true;
  return expMs <= Date.now() + JWT_EXPIRY_SKEW_MS;
};

const setToken = (token: string, source: TokenSource): void => {
  inMemoryToken = token;
  inMemorySource = source;

  if (!firstTokenSourceLogged && AUTH_DEBUG_INFO_ENABLED) {
    firstTokenSourceLogged = true;
    console.info('[DiagnosticsAuthDebug] first token source selected', {
      source,
      embedded: window.parent !== window,
    });
  }
};

export const clearTokenState = (): void => {
  inMemoryToken = null;
  inMemorySource = 'none';
};

const extractTokenFromPayload = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;

  const value = payload as Record<string, unknown>;
  const candidates = [
    value.accessToken,
    value.token,
    (value.hostAuthResult as Record<string, unknown> | undefined)?.accessToken,
    (value.authResult as Record<string, unknown> | undefined)?.accessToken
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
};

const applyHostToken = (token: string): void => {
  if (isTokenExpired(token)) {
    if (inMemorySource === 'host_bootstrap') {
      clearTokenState();
    }
    return;
  }

  setToken(token, 'host_bootstrap');
};

export const initAuthTokenRuntime = (): void => {
  if (runtimeInitialized) return;

  window.addEventListener('message', (event: MessageEvent<unknown>) => {
    const raw = event.data;
    if (!raw || typeof raw !== 'object') return;

    const message = raw as { type?: string; payload?: unknown };
    const type = message.type;
    const payload = message.payload;

    if (!isTrustedHostMessage(event, type)) return;

    if (type === 'HOST_BOOTSTRAP' || type === 'HOST_AUTH_RESULT' || type === 'AUTH_BOOTSTRAP' || type === 'AUTH_TOKEN_RESPONSE') {
      const token = extractTokenFromPayload(payload);
      if (token) {
        applyHostToken(token);
      } else {
        const hostedType = toHostedAuthMessageType(type);
        if (hostedType) {
          recordHostMessageStat('rejected', hostedType, 'payload_parsing_failure');
          console.warn('[DiagnosticsAuthDebug] host message payload parsing failure', {
            type: hostedType,
            reason: 'payload_parsing_failure',
            origin: event.origin,
          });
          logHostMessageStats();
        }
      }
      return;
    }

    if (type === 'AUTH_EVENT' && payload && typeof payload === 'object') {
      const authEvent = payload as { event?: string };
      if (authEvent.event === 'signedOut') {
        clearTokenState();
        return;
      }
      if (authEvent.event === 'tokenUpdated') {
        const token = extractTokenFromPayload(payload);
        if (token) {
          applyHostToken(token);
        } else {
          recordHostMessageStat('rejected', 'AUTH_EVENT', 'payload_parsing_failure');
          console.warn('[DiagnosticsAuthDebug] host auth event payload parsing failure', {
            type: 'AUTH_EVENT',
            authEvent: authEvent.event,
            reason: 'payload_parsing_failure',
            origin: event.origin,
          });
          logHostMessageStats();
          clearTokenState();
        }
      }
    }
  });

  runtimeInitialized = true;
};

const getBridgeToken = async (forceRefresh: boolean): Promise<string | null> => {
  const asyncBridge = CommunicationBridge as unknown as {
    getAccessTokenAsync?: (options?: { forceRefresh?: boolean; useLegacyFallback?: boolean }) => Promise<string | null>;
    onGetAccessToken?: (forceRefresh?: boolean) => Promise<string | null>;
    getAccessToken?: () => string | null;
  };

  if (typeof asyncBridge.getAccessTokenAsync === 'function') {
    const token = await asyncBridge.getAccessTokenAsync({
      forceRefresh,
      useLegacyFallback: false,
    });
    if (token) return token;
  }

  if (typeof asyncBridge.onGetAccessToken === 'function') {
    const token = await asyncBridge.onGetAccessToken(forceRefresh);
    if (token) return token;
  }

  if (typeof asyncBridge.getAccessToken === 'function') {
    return asyncBridge.getAccessToken();
  }

  return null;
};

export const resolveEffectiveAccessToken = async (
  options: { forceBridgeFetch?: boolean } = {}
): Promise<ResolvedAccessToken> => {
  initAuthTokenRuntime();

  const forceBridgeFetch = options.forceBridgeFetch === true;

  if (!forceBridgeFetch && inMemorySource === 'bridge_fetch' && inMemoryToken) {
    const expired = isTokenExpired(inMemoryToken);
    if (!expired) {
      return { token: inMemoryToken, source: inMemorySource };
    }
    clearTokenState();
  }

  if (!forceBridgeFetch && inMemorySource === 'host_bootstrap' && inMemoryToken) {
    const expired = isTokenExpired(inMemoryToken);
    if (!expired) {
      return { token: inMemoryToken, source: inMemorySource };
    }
    clearTokenState();
  }

  const bridgeToken = await getBridgeToken(forceBridgeFetch);
  if (bridgeToken) {
    const expired = isTokenExpired(bridgeToken);
    if (!expired) {
      setToken(bridgeToken, 'bridge_fetch');
      return { token: bridgeToken, source: 'bridge_fetch' };
    }
    clearTokenState();
  }

  return { token: null, source: 'none' };
};

export const refreshAccessTokenFromBridge = async (): Promise<ResolvedAccessToken> => {
  const resolved = await resolveEffectiveAccessToken({ forceBridgeFetch: true });
  return resolved;
};
